import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styled from '@emotion/styled';
import Button from 'views/Dashboards-B2B/components/Button';

export default function SubscriptionSuccess() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [countdown, setCountdown] = useState(3); // Reduced to 3 seconds

    const sessionId = searchParams.get('session_id');

    // Note: Subscription is processed automatically via Stripe webhooks
    // No need to call process-checkout-session endpoint (it was removed)

    useEffect(() => {
        // Countdown timer
        const timer = setInterval(() => {
            setCountdown((prev) => {
                if (prev <= 1) {
                    clearInterval(timer);
                    navigate('/account/financials');
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(timer);
    }, [navigate]);

    const handleGoToBilling = () => {
        navigate('/account/financials?tab=my-billing');
    };

    return (
        <Container>
            <SuccessCard>
                <CheckmarkIcon>✓</CheckmarkIcon>

                <Title>Subscription Payment Successful!</Title>

                <Message>
                    Your subscription payment was successful! Your subscription is being activated
                    and you will receive a confirmation email shortly.
                </Message>

                <Details>
                    <DetailItem>
                        <DetailLabel>Payment Status:</DetailLabel>
                        <DetailValue>✓ Completed</DetailValue>
                    </DetailItem>

                    {sessionId && (
                        <DetailItem>
                            <DetailLabel>Session ID:</DetailLabel>
                            <DetailValue style={{ fontSize: '12px', wordBreak: 'break-all' }}>
                                {sessionId}
                            </DetailValue>
                        </DetailItem>
                    )}
                </Details>

                <InfoBox>
                    <InfoIcon>ℹ️</InfoIcon>
                    <InfoText>
                        You can manage your subscription, update billing settings, and view invoices
                        in the Billing section.
                    </InfoText>
                </InfoBox>

                <ButtonGroup>
                    <Button background="purple" onClick={handleGoToBilling}>
                        Go to Billing Dashboard
                    </Button>
                </ButtonGroup>

                <RedirectMessage>
                    {countdown > 0
                        ? `Redirecting to billing in ${countdown} seconds...`
                        : 'Redirecting...'}
                </RedirectMessage>
            </SuccessCard>
        </Container>
    );
}

const Container = styled.div`
    min-height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    padding: 20px;
`;

const SuccessCard = styled.div`
    background: #ffffff;
    border-radius: 16px;
    max-width: 600px;
    width: 100%;
    padding: 48px;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
    text-align: center;
`;

const CheckmarkIcon = styled.div`
    width: 80px;
    height: 80px;
    background: #10b981;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 48px;
    color: #ffffff;
    margin: 0 auto 24px;
    animation: scaleIn 0.5s ease-out;

    @keyframes scaleIn {
        from {
            transform: scale(0);
        }
        to {
            transform: scale(1);
        }
    }
`;

const Title = styled.h1`
    font-size: 28px;
    font-weight: 700;
    color: #111827;
    margin: 0 0 16px 0;
`;

const Message = styled.p`
    font-size: 16px;
    color: #6b7280;
    margin: 0 0 32px 0;
    line-height: 1.5;
`;

const Details = styled.div`
    background: #f9fafb;
    border-radius: 8px;
    padding: 24px;
    margin-bottom: 24px;
    text-align: left;
`;

const DetailItem = styled.div`
    display: flex;
    justify-content: space-between;
    margin-bottom: 12px;

    &:last-child {
        margin-bottom: 0;
    }
`;

const DetailLabel = styled.span`
    font-size: 14px;
    color: #6b7280;
    font-weight: 500;
`;

const DetailValue = styled.span`
    font-size: 14px;
    color: #111827;
    font-weight: 600;
    text-align: right;
`;

const InfoBox = styled.div`
    background: #eff6ff;
    border: 1px solid #bfdbfe;
    border-radius: 8px;
    padding: 16px;
    display: flex;
    gap: 12px;
    margin-bottom: 32px;
    text-align: left;
`;

const InfoIcon = styled.div`
    font-size: 20px;
    flex-shrink: 0;
`;

const InfoText = styled.p`
    font-size: 14px;
    color: #1e40af;
    margin: 0;
    line-height: 1.5;
`;

const ButtonGroup = styled.div`
    display: flex;
    gap: 12px;
    justify-content: center;
    margin-bottom: 16px;
`;

const RedirectMessage = styled.div`
    font-size: 13px;
    color: #9ca3af;
    font-style: italic;
`;
